export const DANGEROUS_WASTE_CONFIG_COLLECTION = 'dangerous-waste-config';
export const DANGEROUS_WASTE_SORTING_LIST_DOC_ID = 'sorting-list';

export const SORTING_ITEM_FIELDS = [
    'constituent',
    'wasteGroup',
    'unNumber',
    'hazardClasses',
    'declaration',
];

const SORTING_ITEM_ALIASES = Object.freeze({
    constituent: ['wasteType', 'name', 'Affaldet indeholder/består af'],
    wasteGroup: ['waste_group', 'Affaldsgruppe'],
    unNumber: ['un_number', 'UN-nr.'],
    hazardClasses: ['hazard_classes', 'Fareklasse(r)'],
    declaration: ['instruction', 'Deklaration'],
});

const toText = (value) => {
    if (value === undefined || value === null) return '';
    return String(value).replace(/^\uFEFF/, '').trim();
};

const normalizeMatchText = (value) =>
    toText(value)
        .toLowerCase()
        .replace(/[._:/()[\]-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const textContainsMatch = (a, b) => {
    const first = normalizeMatchText(a);
    const second = normalizeMatchText(b);
    if (!first || !second || first === second) return false;
    const longer = first.length >= second.length ? first : second;
    const shorter = first.length >= second.length ? second : first;
    return longer.includes(shorter);
};

const getFieldId = (field) => {
    if (typeof field === 'string') return toText(field);
    return toText(field?.field ?? field?.id);
};

const getFieldIds = (fields = SORTING_ITEM_FIELDS) => {
    const seen = new Set();
    return (Array.isArray(fields) ? fields : SORTING_ITEM_FIELDS)
        .map(getFieldId)
        .filter((field) => {
            if (!field || seen.has(field)) return false;
            seen.add(field);
            return true;
        });
};

const getFieldLabel = (field) => {
    if (typeof field === 'string') return field;
    return toText(field?.label ?? field?.display_name ?? field?.field ?? field?.id);
};

const getSortingFieldValue = (item, field) => {
    if (Object.prototype.hasOwnProperty.call(item, field)) {
        return item[field];
    }

    const aliases = SORTING_ITEM_ALIASES[field] || [];
    for (const alias of aliases) {
        if (Object.prototype.hasOwnProperty.call(item, alias)) {
            return item[alias];
        }
    }

    return '';
};

const hasAnyValue = (item) => {
    if (!item || typeof item !== 'object') return false;
    return Object.keys(item).some((field) => field !== 'id' && toText(item[field]) !== '');
};

export function getDangerousWasteSortingListPath(companyId) {
    return [
        'companies',
        companyId,
        DANGEROUS_WASTE_CONFIG_COLLECTION,
        DANGEROUS_WASTE_SORTING_LIST_DOC_ID,
    ];
}

export function normalizeSortingItem(item = {}, index = 0, fields = SORTING_ITEM_FIELDS) {
    const source = item && typeof item === 'object' ? item : {};
    const normalized = {
        ...source,
        id: toText(source.id) || `sorting-${index + 1}`,
    };

    getFieldIds(fields).forEach((field) => {
        normalized[field] = Object.prototype.hasOwnProperty.call(source, field)
            ? source[field] ?? ''
            : toText(getSortingFieldValue(source, field));
    });

    return normalized;
}

export function sanitizeSortingItems(items = [], fields = SORTING_ITEM_FIELDS) {
    return (Array.isArray(items) ? items : [])
        .map((item, index) => normalizeSortingItem(item, index, fields))
        .filter(hasAnyValue);
}

export function normalizeDangerousWasteSortingListDoc(data = {}, fields = SORTING_ITEM_FIELDS) {
    return {
        version: toText(data.version),
        lastUpdate: data.last_update ?? data.lastUpdate ?? null,
        lastUpdateMs: data.last_update_ms ?? data.lastUpdateMs ?? null,
        updatedBy: toText(data.updated_by ?? data.updatedBy),
        sortingItems: sanitizeSortingItems(data.sorting_items ?? data.sortingItems ?? [], fields),
    };
}

export function serializeDangerousWasteSortingList(config) {
    return {
        version: toText(config.version),
        sorting_items: sanitizeSortingItems(config.sortingItems, config.fields ?? config.columns),
    };
}

function isBlankTsvRow(row) {
    return row.every((cell) => toText(cell) === '');
}

function countDelimiterOutsideQuotes(text, delimiter) {
    let count = 0;
    let inQuotes = false;
    let atCellStart = true;
    const value = String(text || '');

    for (let index = 0; index < value.length; index += 1) {
        const char = value[index];

        if (char === '"' && (inQuotes || atCellStart)) {
            if (inQuotes && value[index + 1] === '"') {
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
            atCellStart = false;
            continue;
        }

        if (!inQuotes && char === delimiter) {
            count += 1;
            atCellStart = true;
            continue;
        }

        if (!inQuotes && (char === '\n' || char === '\r')) {
            atCellStart = true;
            if (char === '\r' && value[index + 1] === '\n') {
                index += 1;
            }
            continue;
        }

        atCellStart = false;
    }

    return count;
}

function detectSeparatedValueDelimiter(text) {
    const value = String(text || '');
    const tabCount = countDelimiterOutsideQuotes(value, '\t');
    const commaCount = countDelimiterOutsideQuotes(value, ',');

    if (tabCount > 0 && tabCount >= commaCount) return '\t';
    return ',';
}

function parseSeparatedValueRows(text, delimiter = detectSeparatedValueDelimiter(text)) {
    const value = String(text || '');
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;
    let atCellStart = true;

    for (let index = 0; index < value.length; index += 1) {
        const char = value[index];

        if (char === '"' && (inQuotes || atCellStart)) {
            if (inQuotes && value[index + 1] === '"') {
                cell += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
            atCellStart = false;
            continue;
        }

        if (!inQuotes && char === delimiter) {
            row.push(cell);
            cell = '';
            atCellStart = true;
            continue;
        }

        if (!inQuotes && (char === '\n' || char === '\r')) {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
            atCellStart = true;
            if (char === '\r' && value[index + 1] === '\n') {
                index += 1;
            }
            continue;
        }

        cell += char;
        atCellStart = false;
    }

    row.push(cell);
    rows.push(row);

    return rows;
}

function serializeTsvCell(value) {
    return toText(value).replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
}

export function serializeSortingListTsv(items = [], fields = SORTING_ITEM_FIELDS) {
    const fieldIds = getFieldIds(fields);
    const headers = getImportFieldOptions(fields).map((field) => field.label);
    const rows = sanitizeSortingItems(items, fields).map((item) => {
        const normalized = normalizeSortingItem(item, 0, fields);
        return fieldIds.map((field) => serializeTsvCell(normalized[field])).join('\t');
    });

    return [headers.map(serializeTsvCell).join('\t'), ...rows].join('\n');
}

function getImportFieldOptions(fields = SORTING_ITEM_FIELDS) {
    return (Array.isArray(fields) ? fields : SORTING_ITEM_FIELDS)
        .map((field) => ({
            field: getFieldId(field),
            label: getFieldLabel(field),
        }))
        .filter((field) => field.field && field.label);
}

function getBestHeaderFieldMatch(header, fields = SORTING_ITEM_FIELDS, usedFields = new Set()) {
    const normalizedHeader = normalizeMatchText(header);
    if (!normalizedHeader) return '';

    const options = getImportFieldOptions(fields)
        .filter((field) => !usedFields.has(field.field));

    const displayNameMatch = options.find(
        (field) => normalizeMatchText(field.label) === normalizedHeader,
    );
    if (displayNameMatch) return displayNameMatch.field;

    const idMatch = options.find(
        (field) => normalizeMatchText(field.field) === normalizedHeader,
    );
    if (idMatch) return idMatch.field;

    const displayNameContainsMatch = options.find(
        (field) => textContainsMatch(field.label, header),
    );
    if (displayNameContainsMatch) return displayNameContainsMatch.field;

    const idContainsMatch = options.find(
        (field) => textContainsMatch(field.field, header),
    );
    return idContainsMatch?.field || '';
}

export function buildSortingListTsvColumnMappings(headers = [], fields = SORTING_ITEM_FIELDS) {
    const usedFields = new Set();
    return (Array.isArray(headers) ? headers : [])
        .map((header, index) => {
            const targetField = getBestHeaderFieldMatch(header, fields, usedFields);
            if (targetField) usedFields.add(targetField);
            return {
                id: `mapping-${index}`,
                sourceIndex: index,
                sourceHeader: toText(header) || `Kolonne ${index + 1}`,
                targetField,
            };
        });
}

export function createSortingListTsvImportDraft(text, fields = SORTING_ITEM_FIELDS) {
    const rows = parseSeparatedValueRows(text);
    if (rows.length === 0) {
        return {
            headers: [],
            rows: [],
            mappings: [],
        };
    }

    const headers = rows[0].map((header, index) =>
        toText(header) || `Kolonne ${index + 1}`,
    );

    const dataRows = rows
        .slice(1)
        .filter((row) => !isBlankTsvRow(row));

    return {
        headers,
        rows: dataRows,
        mappings: buildSortingListTsvColumnMappings(headers, fields),
    };
}

export function parseSortingListTsvRowsWithMappings(rows = [], mappings = [], fields = SORTING_ITEM_FIELDS) {
    const items = [];
    const fieldIds = getFieldIds(fields);
    const fieldIdSet = new Set(fieldIds);
    const cleanMappings = (Array.isArray(mappings) ? mappings : [])
        .map((mapping) => ({
            sourceIndex: Number(mapping?.sourceIndex),
            targetField: toText(mapping?.targetField),
        }))
        .filter((mapping) =>
            Number.isInteger(mapping.sourceIndex) &&
            mapping.sourceIndex >= 0 &&
            fieldIdSet.has(mapping.targetField),
        );

    rows.forEach((row, index) => {
        if (isBlankTsvRow(row)) return;
        const item = { id: `sorting-${items.length + 1}` };
        fieldIds.forEach((field) => {
            item[field] = '';
        });
        cleanMappings.forEach((mapping) => {
            item[mapping.targetField] = row[mapping.sourceIndex] ?? '';
        });
        items.push(
            normalizeSortingItem(item, index, fields),
        );
    });

    return sanitizeSortingItems(items, fields);
}

export function parseSortingListTsv(text, fields = SORTING_ITEM_FIELDS) {
    const draft = createSortingListTsvImportDraft(text, fields);
    return parseSortingListTsvRowsWithMappings(draft.rows, draft.mappings, fields);
}

export function incrementSortingListVersion(version) {
    const current = toText(version);
    if (!current) return '1';

    const match = current.match(/^(.*?)(\d+)(\D*)$/);
    if (!match) return `${current} 1`;

    const [, prefix, numberText, suffix] = match;
    const nextNumber = String(Number(numberText) + 1).padStart(numberText.length, '0');
    return `${prefix}${nextNumber}${suffix}`;
}

export function getSortingItemVoteValue(item, fields = SORTING_ITEM_FIELDS) {
    const normalized = normalizeSortingItem(item, 0, fields);
    return getFieldIds(fields)
        .map((field) => toText(normalized[field]))
        .join(' | ');
}

export function getSortingItemVoteLabel(item, fields = SORTING_ITEM_FIELDS) {
    const normalized = normalizeSortingItem(item, 0, fields);
    return getFieldIds(fields)
        .map((field) => toText(normalized[field]))
        .filter(Boolean)
        .join(' | ');
}

export function buildSortingListVoteSuggestions(items = [], fields = SORTING_ITEM_FIELDS) {
    const fieldOptions = getImportFieldOptions(fields);
    return sanitizeSortingItems(items, fields)
        .map((item, index) => {
            const value = getSortingItemVoteValue(item, fields);
            const label = getSortingItemVoteLabel(item, fields);
            return {
                // An imported list can contain repeated IDs. The position keeps every
                // source row addressable without collapsing rows that share cell values.
                id: `${item.id || 'sorting-row'}-${index}`,
                value,
                label,
                cells: fieldOptions.map((field) => ({
                    id: field.field,
                    label: field.label,
                    value: toText(item[field.field]),
                })),
            };
        })
        .filter((item) => item.label);
}
